// composables
import { ref, computed, type Ref } from 'vue'
// utilities
import dayjs from 'dayjs'
import Swal from 'sweetalert2'
// composables
import useApiRequest from '@/composables/useApiRequest'
// types
import type { Agreement } from '@/types/domain/agreement'

/**
 * Appointment type definition
 */
interface Appointment {
  id: number
  start: string
  time: string
  isCancelled: boolean
  agreementId: number
  outcome?: string | null
  decision?: string | null
}

/**
 * Composable for managing agreement appointments
 * Handles fetching appointments, creating appointments, and sending invites
 */
export default function useAgreementAppointments(agreementID: Ref<number>, _agreementData: Ref<Agreement | null>) {
  // state
  const appointmentHandlerIsOpen = ref(false)
  const appointmentHandlerData = ref<{
    agreementID: string | number
    dealershipID: number
    timeZone: string
    customerTimeZone: string | null
  } | null>(null)
  const inviteTemplatesModalIsOpen = ref(false)
  const randomKey = ref(Math.random())

  // api requests
  const appointmentsApiRequest = useApiRequest()
  const appointmentsData = computed(() => {
    return (appointmentsApiRequest.data.value as Appointment[] | null) || null
  })
  const appointmentsFetchData = appointmentsApiRequest.send
  const appointmentInviteApiRequest = useApiRequest()

  /**
   * Computed counter for future appointments
   * Excludes cancelled appointments and appointments with outcomes
   */
  const appointmentsCounter = computed(() => {
    let counter = 0

    if (appointmentsData.value != null && Array.isArray(appointmentsData.value) && appointmentsData.value.length > 0) {
      appointmentsData.value.forEach((app: Appointment) => {
        // Check if appointment is in the future
        const isUpcoming = dayjs(app.start).isAfter(dayjs())

        // Exclude cancelled appointments
        if (app.isCancelled) {
          return
        }

        // Exclude appointments with outcomes
        if (app.outcome != null) {
          return
        }

        if (isUpcoming) {
          counter++
        }
      })
    }

    return counter
  })

  /**
   * Fetches appointments for the agreement
   */
  function fetchAppointments(): void {
    appointmentsFetchData({ endpoint: '/v1/appointments?agreementId=' + agreementID.value })
  }

  /**
   * Handles new appointment click with validation
   * Excludes cancelled appointments and appointments with outcomes from the check
   */
  function handleNewAppointmentClick(openAppointmentHandler: () => void): void {
    let futureAppointmentsCount = null
    if (appointmentsData.value && Array.isArray(appointmentsData.value)) {
      futureAppointmentsCount = appointmentsData.value.filter((appt: Appointment) => {
        // Check if appointment is in the future
        const isUpcoming = dayjs().isBefore(dayjs(appt.time))

        // Exclude cancelled appointments
        if (appt.isCancelled) {
          return false
        }

        // Exclude appointments with outcomes
        if (appt.outcome != null) {
          return false
        }

        return isUpcoming
      }).length
    }

    if (futureAppointmentsCount) {
      Swal.fire({
        title: 'Are you sure?',
        text: 'The customer already has an upcoming appointment',
        icon: 'warning',
        reverseButtons: true,
        showCloseButton: true,
        showCancelButton: true,
        confirmButtonText: 'Continue',
        confirmButtonColor: 'var(--bg-brand-solid)',
        cancelButtonText: 'Cancel',
        cancelButtonColor: 'var(--bg-secondary)'
      }).then(function (response) {
        if (response.isConfirmed) {
          openAppointmentHandler()
        }
      })
    } else {
      openAppointmentHandler()
    }
  }

  /**
   * Handles appointment invite click with validation
   * Excludes cancelled appointments and appointments with outcomes from the check
   */
  function handleAppointmentInviteClick(fetchMarketingTemplates: () => void): void {
    let futureAppointmentsCount = null
    if (appointmentsData.value && Array.isArray(appointmentsData.value)) {
      futureAppointmentsCount = appointmentsData.value.filter((appt: Appointment) => {
        // Check if appointment is in the future
        const isUpcoming = dayjs().isBefore(dayjs(appt.time))

        // Exclude cancelled appointments
        if (appt.isCancelled) {
          return false
        }

        // Exclude appointments with outcomes
        if (appt.outcome != null) {
          return false
        }

        return isUpcoming
      }).length
    }

    if (futureAppointmentsCount) {
      Swal.fire({
        title: 'Are you sure?',
        text: 'The customer already has an upcoming appointment',
        icon: 'warning',
        reverseButtons: true,
        showCloseButton: true,
        showCancelButton: true,
        confirmButtonText: 'Continue',
        confirmButtonColor: 'var(--bg-brand-solid)',
        cancelButtonText: 'Cancel',
        cancelButtonColor: 'var(--bg-secondary)'
      }).then(function (response) {
        if (response.isConfirmed) {
          inviteTemplatesModalIsOpen.value = true
          fetchMarketingTemplates()
        }
      })
    } else {
      inviteTemplatesModalIsOpen.value = true
      fetchMarketingTemplates()
    }
  }

  /**
   * Opens the appointment handler modal
   */
  function openAppointmentHandler(): void {
    randomKey.value = Math.random()
    appointmentHandlerIsOpen.value = true
  }

  /**
   * Handles when a new appointment is submitted
   */
  function onAppointmentUpdated(appointmentData: { id: number }): void {
    if (!appointmentsData.value || !Array.isArray(appointmentsData.value)) {
      return
    }
    const appointment = appointmentsData.value.find((appointment: Appointment) => appointment.id == appointmentData.id)
    if (appointment) {
      const index = appointmentsData.value.indexOf(appointment)
      if (index > -1) {
        appointmentsData.value.splice(index, 1)
      }
    }
  }

  /**
   * Handles when a new appointment is submitted
   */
  function newAppointmentSubmitted(): void {
    appointmentsFetchData({ endpoint: '/v1/appointments?agreementId=' + agreementID.value })
    appointmentHandlerIsOpen.value = false
  }

  return {
    // state
    appointmentsData,
    appointmentHandlerIsOpen,
    appointmentHandlerData,
    inviteTemplatesModalIsOpen,
    randomKey,
    appointmentsCounter,
    appointmentInviteApiRequest,
    // methods
    fetchAppointments,
    handleNewAppointmentClick,
    handleAppointmentInviteClick,
    openAppointmentHandler,
    onAppointmentUpdated,
    newAppointmentSubmitted
  }
}
